import './style.scss';

import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';

import { getAll, deleteById, restore } from 'apis/bonusExpenditure.api';
import { Can } from 'components/Can';
import { mapError } from 'utils/func';
import { TIMEZONE_VN } from 'constant';
import Confirm from './components/Confirm';
import Form from './components/Form';

export default function BonusExpenditureViewList() {
  // #region Data
  const { t } = useTranslation();
  const dialogFormRef = useRef(null);
  const dialogConfirmationRef = useRef(null);
  const { mutate: mutateDelete } = useMutation(deleteById);
  const { mutate: mutateRestore } = useMutation(restore);

  const { data: _data, isLoading, isError, error, refetch } = useQuery(['bonus_expenture'], getAll);

  const data = useMemo(() => _data?.data ?? [], [_data]);
  // #endregion

  // #region Render
  const formatIndex = (_, { rowIndex }) => rowIndex + 1;

  const formatActions = (value) => (
    <div className="flex justify-around gap-1">
      {value?.deletedAt === null ? (
        <>
          <Can do="update" on="bonus_expenditure">
            <Button
              severity="warning"
              tooltip={t('bonusExpenditure.update.title')}
              tooltipOptions={{ position: 'left' }}
              icon="pi pi-pencil"
              onClick={() => {
                dialogFormRef.current.open({
                  mode: 'UPDATE',
                  value,
                });
              }}
            />
          </Can>
          <Can do="delete" on="bonus_expenditure">
            <Button
              severity="danger"
              tooltip={t('bonusExpenditure.delete.title')}
              tooltipOptions={{ position: 'left' }}
              icon="pi pi-trash"
              onClick={() => {
                dialogConfirmationRef.current.open({
                  mode: 'delete',
                  mutate: mutateDelete,
                  value,
                });
              }}
            />
          </Can>
        </>
      ) : (
        <Can do="restore" on="bonus_expenditure">
          <Button
            icon="pi pi-undo"
            severity="danger"
            tooltip={t('bonusExpenditure.restore.title')}
            tooltipOptions={{ position: 'left' }}
            onClick={() =>
              dialogConfirmationRef.current.open({
                mode: 'restore',
                mutate: mutateRestore,
                value,
              })
            }
          />
        </Can>
      )}
    </div>
  );

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString('en-GB', { timeZone: TIMEZONE_VN }) : '';

  const formatStartDate = (value) => formatDate(value?.startDate);

  const formatEndDate = (value) => formatDate(value?.endDate);
  // #endregion Render

  return (
    <div className="card relative">
      {isError && <h3 className="text-red-600">{mapError(t, error)}</h3>}
      <div className="flex justify-content-between align-items-center pb-4">
        <h4 className="m-0 hidden sm:block text-xl sm:text-2xl">
          {t('bonusExpenditure.viewList.title')}
        </h4>
        <Can do="create" on="bonus_expenditure">
          <Button
            className="bg-cyan-600 border-cyan-600"
            label={t('bonusExpenditure.add')}
            icon="pi pi-plus"
            onClick={() => {
              dialogFormRef.current.open({
                mode: 'CREATE',
              });
            }}
          />
        </Can>
      </div>

      <DataTable
        value={data}
        className="p-datatable-gridlines header-table"
        showGridlines
        scrollable
        dataKey="id"
        loading={isLoading}
        responsiveLayout="scroll"
        emptyMessage={t('bonusExpenditure.viewList.emptyMessage')}
      >
        <Column
          header={t('bonusExpenditure.viewList.no')}
          body={formatIndex}
          className="text-center w-5rem"
          frozen
        />
        <Column
          header={t('bonusExpenditure.nameBonus')}
          field="name"
          className="w-30rem"
          style={{ minWidth: '25rem' }}
          frozen
        />
        <Column
          header={t('bonusExpenditure.researchField')}
          field="researchField.name"
          className="w-30rem"
          style={{ minWidth: '20rem' }}
        />
        <Column
          header={t('bonusExpenditure.expenditure')}
          field="expenditure"
          className="w-30rem text-center"
          style={{ minWidth: '14rem' }}
        />
        <Column
          header={t('bonusExpenditure.startDate')}
          body={formatStartDate}
          className="w-30rem text-center"
          style={{ minWidth: '10rem' }}
        />
        <Column
          header={t('bonusExpenditure.endDate')}
          body={formatEndDate}
          className="w-30rem text-center"
          style={{ minWidth: '10rem' }}
        />
        <Column
          header={t('projectProposal.action')}
          body={formatActions}
          className="w-8rem text-center"
          frozen
          alignFrozen="right"
        />
      </DataTable>

      <Form ref={dialogFormRef} refetch={refetch} />

      <Can do="delete" on="bonus_expenditure">
        <Confirm ref={dialogConfirmationRef} refetch={refetch} />
      </Can>
    </div>
  );
}
